import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import moment from 'moment'
import config from 'scoreboard/components/config'
import i18n from 'scoreboard/components/i18n'
import momentHelper from 'scoreboard/components/moment-helper'
import permissions from 'scoreboard/components/permissions'
import isBehindSchedule from './is-behind-schedule'
import { BEHIND_RED, BEHIND_YELLOW } from './schedule-status'
import formatValue from 'scoreboard/components/format-value'
import { PERCENT } from 'scoreboard/datasets/model/data-type'

var Initiative = Backbone.Model.extend({

	section: 'initiatives',
	domainType: 'initiative',

	urlRoot: function(options) {
		return 'api/initiatives'
	},

	isEditable: function() {
		return permissions.modifyInitiatives(this.get('organizationId')) && permissions.isEditableIfReplicated(this)
	},

	/*
	 * Saves a status update on the initiative item.
	 * @param {Object} status
	 * @param {Boolean} [treeCalculations=false] pass true to have the server return the org's initiative tree w/ updated calculations from the status update.
	 */
	saveStatus: function(status, treeCalculations) {
		var oldStatuses = _.cloneDeep(this.get('statuses'))
		const url = 'api/initiatives/' + this.id + `/status?treeCalculations=${!!treeCalculations}`
		return $.put(url, JSON.stringify(status), data => {
			status.id = data.id
			this.clear({silent: true})
			this.set(data.initiative)

			Backbone.trigger('edit:domain-object', this)

			this.trigger('save:status', status, oldStatuses, data)
		})
	},

	/*
	 * Delete a status update on the initiative item.
	 * @param {Number} statusId
	 * @param {Boolean} [treeCalculations=false] pass true to have the server return the org's initiative tree w/ updated calculations from the status update.
	 */
	deleteStatus: function (statusId, treeCalculations) {
		var oldStatuses = _.cloneDeep(this.get('statuses'))
		var deletedStatus = _.find(oldStatuses, function (s) { return s.id == statusId })
		$.delete('api/initiatives/' + this.id + '/status/' + statusId + `?treeCalculations=${!!treeCalculations}`, function(data) {
			this.clear({silent: true})
			this.set(data.initiative)

			Backbone.trigger('edit:domain-object', this)

			this.trigger('remove:status', deletedStatus, oldStatuses, data)
		}.bind(this))
	},

	status: function (id) {
		return _.find(this.get('statuses'), function (s) {
			return s.id === id
		})
	},

	/**
	 * Returns "real" statuses for this initiative if they exist, or calculated statuses if they are being calculated
	 * from the descendants
	 */
	statuses: function () {
		var statusType = this.statusType()
		if (statusType === 'own') {
			return this.get('statuses') || []
		} else if (statusType === 'descendant') {
			// Without a budget, we won't have any calculated data to work with. So just return the descendant statuses
			var descendants = _.keyBy(this.get('descendantsWithStatuses'), 'id')
			return _(this.get('descendantStatuses')).map(function (status) {
				return {
					date: status.date,
					percentComplete: status.percentComplete,
					expendedToDate: status.expendedToDate,
					label: descendants[status.initiativeId].label
				}
			}).sortBy('date').value()
		} else if (statusType === 'calculated') {
			// Use the calculated data series points to build fake statuses
			var pctComplete = this.percentCompleteSeriesPoints()
			var expended = this.expendedSeriesPoints()

			if (!_.isEmpty(pctComplete) && !_.isEmpty(expended)) {
				if (noRawValue(pctComplete[0]) && noRawValue(expended[0])) {
					// First calculation is just the initial state with no actual
					// statuses entered; skip it
					pctComplete.splice(0, 1)
					expended.splice(0, 1)
				}
			}

			return _.map(pctComplete, function (pct, i) {
				var ex = expended[i]

				return {
					date: pct.title,
					percentComplete: pct.raw,
					expendedToDate: ex.raw
				}
			})
		}
	},

	/**
	 * Returns the type of statuses used for this initiative:
	 *  - own: uses its own manually entered statuses
	 *  - calculated: uses data calculated by aggregating descendants (ie EVM calculations)
	 *  - descendant: uses unaggregated statuses from the descendants (ie EVM calculations can't be made)
	 */
	statusType: function () {
		// If there are any "real" statuses, they are used regardless of any descendants
		var own = this.get('statuses')
		if (own && own.length > 0) {
			return 'own'
		}

		if (this.get('descendantsHaveStatuses')) {
			// If there aren't any "real" statuses, but there are descendants with statuses, then the statuses for
			// this initiative come from the descendants.
			// If there is an aggregated budget, startDate, and endDate, they are calculated using EVM calculations.
			// If not, we just display the descendant statuses (unaggregated)
			return _.isNil(this.get('aggregatedBudget')) || _.isNil(this.get('aggregatedStartDate')) || _.isNil(this.get('aggregatedEndDate'))
					? 'descendant' : 'calculated'
		} else {
			// If there aren't any real statuses or children with statuses, then we're technically using the
			// initiative's own statuses, even though there aren't any yet
			return 'own'
		}
	},

	percentCompleteSeriesPoints: function () {
		return _.map(this.get('percentCompletePoints'), function (point, i) {
			return {
				title: point.date,
				raw: point.value,
				color: 'initiative-schedule',
				rangeId: i
			}
		})
	},

	expendedSeriesPoints: function () {
		return _.map(this.get('actualCostOfWorkPerformed'), function (acws, i) {
			return {
				title: acws.date,
				raw: acws.value,
				color: 'initiative-budget',
				rangeId: i
			}
		})
	},

	/**
	 * If the initiative's due date has passed, returns an alternative "late"
	 * ScheduleStatus even if the projection is "on time" or "ahead of schedule".
	 * Will return null if the initiative's calculated ScheduleStatus should
	 * still be used.
	 *
	 * @param {Object} opt
	 * @param {Boolean} [opt.referenceDate] Date to use as a reference point; defaults
	 * 					to the current date, but is set to a different value when
	 * 					displaying a dashboard for a period in the past. Also useful
	 * 					for unit testing.
	 *
	 * @returns {ScheduleStatus|null}
	 */
	getPastDueDateStatus (opt = {}) {
		let today = moment(opt.referenceDate) || moment()

		// Note: Similar logic is in getCurrentInitiativeColor.kt; if this needs
		// to be changed, that probably should be too

		let projectedSchedule = this.get('projectedSchedule')
		if (projectedSchedule && isBehindSchedule(projectedSchedule.status)) {
			// It will have an appropriate "late" color anyway, so no need to
			// compare dates
			return null
		}

		let startDate = this.get('aggregatedStartDate') ? moment(this.get('aggregatedStartDate')) : null
		let endDate = this.get('aggregatedEndDate') ? moment(this.get('aggregatedEndDate')) : null
		let isComplete = !!this.get('aggregatedCompletionDate')
		if (!isComplete && endDate != null && today.isAfter(endDate)) {
			// If the initiative is not complete and we're past the end date,
			// should be red (#24305) or yellow (#61007), regardless of the
			// calculated color

			let scheduleRedThreshold = config.initiativesScheduleRedThreshold
			if (_.isNil(scheduleRedThreshold)) {
				// If there's no red threshold, late is always red
				return BEHIND_RED
			}
			if (startDate == null) {
				// If we can't calculate the percent difference from schedule
				// (like for milestones), just make it red
				return BEHIND_RED
			}

			let percentLate = getPercentLate({ today, startDate, endDate })
			return percentLate >= scheduleRedThreshold ? BEHIND_RED : BEHIND_YELLOW
		}

		return null
	},

	/**
	 * Returns true if the initiative's projected end date is in the past.
	 *
	 * @param {Object} opt
	 * @param {Boolean} [opt.referenceDate] Date to use as a reference point; defaults
	 * 					to the current date, but is set to a different value when
	 * 					displaying a dashboard for a period in the past. Also useful
	 * 					for unit testing.
	 *
	 */
	pastProjectedDate(opt = {}) {
		let today = moment(opt.referenceDate) || moment()

		let projectedSchedule = this.get('projectedSchedule')
		let complete = !!this.get('aggregatedCompletionDate')

		let projectedEndDate = projectedSchedule.projectedEndDate || this.get('aggregatedEndDate')

		if (_.isNil(projectedEndDate)) {
			return false
		}

		return !complete && (today.isSame(projectedEndDate) || today.isAfter(projectedEndDate))
	},

	/**
	 * Returns a warning message to explain the initiative's status. For example, will
	 * explain why an incomplete initiative has a projected end date in the past, or
	 * why an "on schedule" initiative is red.
	 *
	 * @param {Object} opt
	 * @param {Boolean} [opt.referenceDate] Date to use as a reference point; defaults
	 * 					to the current date, but is set to a different value when
	 * 					displaying a dashboard for a period in the past. Also useful
	 * 					for unit testing.
	 *
	 */
	scheduleWarning(opt = {}) {
		let today = moment(opt.referenceDate) || moment()

		let statuses = this.statuses()
		let projectedSchedule = this.get('projectedSchedule')

		let lastStatus = _.last(statuses)
		if (this.get('type') === 'milestone' && lastStatus) {
			// The only possible "projected date" for milestones is the due date.
			// If the last status is after the due date, then we really don't have
			// anything to project. It'll be red, and there's really no additional
			// message we can give that makes sense.
			let statusDate = moment(lastStatus.date)
			if (statusDate.isAfter(this.get('aggregatedEndDate'))) {
				return null
			}
		}

		let pastDueDateStatus = this.getPastDueDateStatus(opt)
		if (pastDueDateStatus) {
			let redThreshold = formatValue({
				value: config.initiativesScheduleRedThreshold,
				dataType: PERCENT
			})

			// Use i18n to apply configurable terminology changes to the
			// initiative type #55589
			let configuredType = i18n.get('initiatives.type.' + _.lowerCase(this.get('type')))
			let colorMessageCode = 'initiatives.scheduleWarning.pastDueDate.' +
				(pastDueDateStatus === BEHIND_RED ? 'red' : 'yellow')
			let colorMessage = i18n.get(colorMessageCode,
				_.lowerCase(configuredType), redThreshold)

			if (_.isEmpty(statuses)) {
				return colorMessage + ' ' + i18n.get('initiatives.scheduleWarning.addStatus')
			} else {
				let lastStatusMessage = i18n.get('initiatives.scheduleWarning.howeverLastStatus',
					lastStatusDateMessage(statuses, today))
				let projectionMessage = i18n.get('initiatives.scheduleWarning.estimatedCompletion',
					projectedDateMessage(this, projectedSchedule, today))
				let moreRecentStatusMessage = i18n.get('initiatives.scheduleWarning.addMoreRecentStatus')

				return `<div>${colorMessage} ${lastStatusMessage} ${projectionMessage}</div><div>${moreRecentStatusMessage}</div>`
			}
		} else if (this.pastProjectedDate(opt)) {
			// If there are statuses, display the default warning, else, display the no-statuses message
			if (statuses && !_.isEmpty(statuses)) {
				let lastStatusMessage = i18n.get('initiatives.scheduleWarning.lastStatus',
					lastStatusDateMessage(statuses, today))
				let projectionMessage = i18n.get('initiatives.scheduleWarning.estimatedCompletion',
					projectedDateMessage(this, projectedSchedule, today))
				let moreRecentStatusMessage = i18n.get('initiatives.scheduleWarning.addMoreRecentStatus')

				return `${lastStatusMessage} ${projectionMessage} ${moreRecentStatusMessage}`
			} else {
				// This shouldn't actually happen; if there are no statuses, the "projected end date"
				// is just the due date. If we're past the end date, the forceRedScheduleStatus() block
				// above should have taken care of things. But I'll print out an error just in case it
				// does happen
				console.error('Past projected date, but no statuses; this should not be possible')
				return null
			}
		}
	}
})

function todayText () {
	// Wrapped in a <span> so CSS will make it lowercase
	return '<span>' + _.escape(i18n.get('common.today')) + '</span>'
}

function lastStatusDateMessage (statuses, today) {
	let lastStatusIsToday = today.isSame(momentHelper.isoDate(_.last(statuses).date), 'day')
	let statusFromToday = momentHelper.fromDate(today, _.last(statuses).date)

	return lastStatusIsToday ? todayText() : statusFromToday
}

function projectedDateMessage (initiative, projectedSchedule, today) {
	let projectedDate = projectedSchedule.projectedEndDate || initiative.get('aggregatedEndDate')

	let isProjectedToday = today.isSame(momentHelper.isoDate(projectedDate), 'day')
	let projectedFromToday = momentHelper.fromDate(today, projectedDate)

	return isProjectedToday ? todayText() : projectedFromToday
}

function noRawValue (point) {
	return _.isNil(point.raw) || point.raw === 0
}

/**
 * When creating a new tree node there are rules about the type that can be
 * added based on the ancestor(s).  Namely, a task can't have a descendant
 * that is an initiative. (#26156)
 *
 * @param {KalpaTree} tree
 * @param {Number} relativeId The initiative id that this node is relative to
 *
 * @returns {String} The type that should be used when creating/labeling the
 * new tree node
 */
const getDefaultTypeForTree = function(tree, relativeId) {
	let type = 'initiative'
	if (relativeId) {
		let ancestor = tree.get(relativeId)
		while (ancestor.parentId != null) {
			ancestor = tree.parent(ancestor)
			if (ancestor.type === 'task') {
				type = 'task'
				break
			}
		}
	}
	return type
}

function getPercentLate ({ today, startDate, endDate }) {
	let daysLate = today.diff(endDate, 'days')
	let projectLength = moment(endDate).diff(startDate, 'days')
	return daysLate / projectLength
}

export default Initiative
export { getDefaultTypeForTree }
Initiative.__filename__ = 'initiatives/initiative.js'
